// Sectores enlazados a bodegas o zonas y propuesta de carga de fuego desde el inventario (PE-28a, aprobada por Kevin;
// PE-31: qi solo del RD 164). PE-A4, M37 (emergency_plan_sector_locations, emergency_fire_load_items.origin_note).
//  - Un sector se enlaza a una o varias bodegas (toda la bodega) o zonas de la empresa. Manager y admin enlazan;
//    solo admin quita un enlace (RLS de M37).
//  - "Proponer carga de fuego desde el inventario": una fila por producto de esas bodegas o zonas. Los productos de una
//    zona salen de warehouse_zones.product_ids y de product_locations (racks).
//  - Material del RD 164 (Tabla 1.3.3, filas en MJ/kg) SOLO si el CAS o el nombre del producto coinciden con una fila
//    después de normalizar (minúsculas, sin tildes ni signos; el nombre también sin lo que va entre paréntesis). Sin
//    coincidencia, o con varias filas posibles, la fila queda sin material. La Tabla 1.3.5 es por actividad (MJ/m² o
//    MJ/m³), no por producto: no se usa para proponer filas por masa. products no tiene sinónimos.
//  - Cantidad: el inventario de StorSafe NO guarda cantidades (ni products ni product_locations tienen cantidad ni
//    unidad). Por eso hoy toda fila propuesta queda "por validar" sin masa; aKg() convierte kg, g y t cuando haya dato.
//  - Nada se guarda sin que el responsable elija las filas y después guarde el sector.

import { supabase } from '../../supabaseClient';
import { mensajeErrorPE, MENSAJE_SIN_PERMISO } from './pe-datos';
import { FilaMaterialRD164, MATERIALES_RD164 } from './pe-materiales-rd164';

export interface Bodega { id: string; name: string; zonas: { id: string; name: string; product_ids: string[] }[] }
export interface EnlaceSector { id?: string; sector_id: string; warehouse_id: string; zone_id: string | null }
export interface ProductoInventario { id: string; name: string; cas_number: string | null; cantidad?: { valor: number; unidad: string } | null }
export interface UbicacionProducto { product_id: string; zone_id: string }

export type EstadoPropuesta = 'listo' | 'por_validar';
export interface PropuestaCarga {
  productId: string;
  producto: string;
  lugar: string;                         // "bodega X" o "bodega X, zona Y"
  material: FilaMaterialRD164 | null;
  coincidencia: 'cas' | 'nombre' | null;
  masaKg: number | null;
  estado: EstadoPropuesta;
  notas: string[];
  origen: string;                        // origin_note
}

export const NOTA_SIN_CANTIDAD = 'El inventario no tiene la cantidad en kg de este producto: ingrésala (queda por validar).';
export const NOTA_SIN_MATERIAL = 'Sin coincidencia con la Tabla 1.3.3 del RD 164: elige el material o ingrésalo a mano con su fuente.';

export function normalizarTexto(s: string | null | undefined, sinParentesis = false): string {
  let t = (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  if (sinParentesis) t = t.replace(/\([^)]*\)/g, ' ');
  return t.replace(/[^a-z0-9]+/g, ' ').trim();
}

export const normalizarCas = (cas: string | null | undefined) => {
  const t = (cas ?? '').replace(/\s/g, '');
  return /^\d{2,7}-\d{2}-\d$/.test(t) ? t : '';
};

// Coincidencia con la Tabla 1.3.3. Primero el CAS; si no, el nombre. Una sola fila o ninguna.
export function materialDeProducto(p: Pick<ProductoInventario, 'name' | 'cas_number'>):
  { material: FilaMaterialRD164 | null; coincidencia: 'cas' | 'nombre' | null; nota: string | null } {
  const cas = normalizarCas(p.cas_number);
  const unico = (filas: FilaMaterialRD164[], tipo: 'cas' | 'nombre') => {
    const masa = filas.filter(m => m.unidad === 'MJ/kg');
    if (masa.length === 1) return { material: masa[0], coincidencia: tipo, nota: null };
    if (masa.length > 1) return { material: null, coincidencia: null, nota: `Varias filas de la Tabla 1.3.3 con ese ${tipo === 'cas' ? 'CAS' : 'nombre'} (${masa.map(m => m.nombre).join(' / ')}): elige una.` };
    if (filas.length) return { material: null, coincidencia: null, nota: `La fila de la Tabla 1.3.3 (${filas[0].nombre}) no va por kg (${filas[0].unidad}): no se propone por masa.` };
    return null;
  };
  if (cas) {
    const r = unico(MATERIALES_RD164.filter(m => m.cas === cas), 'cas');
    if (r) return r;
  }
  const n = normalizarTexto(p.name), nSin = normalizarTexto(p.name, true);
  if (n) {
    const r = unico(MATERIALES_RD164.filter(m => {
      const a = normalizarTexto(m.nombre), b = normalizarTexto(m.nombre, true);
      return a === n || b === n || (nSin && (a === nSin || b === nSin));
    }), 'nombre');
    if (r) return r;
  }
  return { material: null, coincidencia: null, nota: null };
}

// Cantidad del inventario → kg. Solo kg, g y t (conversión exacta). Otra unidad (L, galones, unidades…) necesita
// la densidad o el peso, que products no tiene: null.
export function aKg(c: { valor: number; unidad: string } | null | undefined): number | null {
  if (!c || !Number.isFinite(c.valor) || c.valor < 0) return null;
  const u = normalizarTexto(c.unidad);
  if (['kg', 'kilo', 'kilos', 'kilogramo', 'kilogramos'].includes(u)) return c.valor;
  if (['g', 'gr', 'gramo', 'gramos'].includes(u)) return c.valor / 1000;
  if (['t', 'tonelada', 'toneladas'].includes(u)) return c.valor * 1000;
  return null;
}

// Nombre de la fila de la Tabla 1.3.3 sin el punto final con que la transcribe el BOE.
export const nombreMaterial = (m: Pick<FilaMaterialRD164, 'nombre'>) => m.nombre.replace(/\.$/, '');

export const lugarDe = (b: Bodega, zonaId: string | null) => {
  const z = zonaId ? b.zonas.find(x => x.id === zonaId) : undefined;
  return z ? `bodega ${b.name}, zona ${z.name}` : `bodega ${b.name}`;
};

// Una propuesta por producto (el primer lugar donde aparece). Las filas que el sector ya tiene con ese producto no
// se proponen otra vez.
export function proponerCargaDesdeInventario(
  enlaces: Pick<EnlaceSector, 'warehouse_id' | 'zone_id'>[], bodegas: Bodega[], productos: ProductoInventario[],
  ubicaciones: UbicacionProducto[], yaEnSector: Set<string> = new Set(),
): PropuestaCarga[] {
  const porId = new Map(productos.map(p => [p.id, p]));
  const vistos = new Set<string>();
  const out: PropuestaCarga[] = [];
  for (const e of enlaces) {
    const b = bodegas.find(x => x.id === e.warehouse_id);
    if (!b) continue;
    const zonas = e.zone_id ? b.zonas.filter(z => z.id === e.zone_id) : b.zonas;
    for (const z of zonas) {
      const ids = [...z.product_ids, ...ubicaciones.filter(u => u.zone_id === z.id).map(u => u.product_id)];
      for (const id of ids) {
        const p = porId.get(id);
        if (!p || vistos.has(id) || yaEnSector.has(id)) continue;
        vistos.add(id);
        const lugar = lugarDe(b, e.zone_id);
        const m = materialDeProducto(p);
        const masaKg = aKg(p.cantidad);
        const notas = [m.nota ?? (m.material ? null : NOTA_SIN_MATERIAL), masaKg === null ? NOTA_SIN_CANTIDAD : null].filter((x): x is string => !!x);
        out.push({
          productId: p.id, producto: p.name, lugar, material: m.material, coincidencia: m.coincidencia, masaKg,
          estado: m.material && masaKg !== null ? 'listo' : 'por_validar', notas,
          // PE-A4b: con coincidencia, la traza dice qué material del RD 164 se tomó y por qué.
          origen: `Propuesto desde el inventario (${lugar}): producto ${p.name}${m.material
            ? ` → ${nombreMaterial(m.material)} (RD 164, Tabla 1.3.3, por ${m.coincidencia === 'cas' ? 'CAS' : 'nombre'})`
            : ', sin material del RD 164'}`.slice(0, 300),
        });
      }
    }
  }
  return out;
}

// ── Datos ──

export async function cargarBodegas(companyId: string): Promise<Bodega[]> {
  const { data: w, error } = await supabase.from('warehouses').select('id, name').eq('company_id', companyId).order('name');
  if (error) throw new Error(mensajeErrorPE(error, 'leer las bodegas'));
  const ids = (w ?? []).map((x: any) => x.id);
  if (!ids.length) return [];
  const { data: z, error: ez } = await supabase.from('warehouse_zones').select('id, name, warehouse_id, product_ids').in('warehouse_id', ids).order('name');
  if (ez) throw new Error(mensajeErrorPE(ez, 'leer las zonas'));
  return (w ?? []).map((x: any) => ({
    id: x.id, name: x.name,
    zonas: (z ?? []).filter((y: any) => y.warehouse_id === x.id).map((y: any) => ({ id: y.id, name: y.name, product_ids: y.product_ids ?? [] })),
  }));
}

export async function cargarEnlaces(sectorIds: string[]): Promise<EnlaceSector[]> {
  if (!sectorIds.length) return [];
  const { data, error } = await supabase.from('emergency_plan_sector_locations').select('id, sector_id, warehouse_id, zone_id').in('sector_id', sectorIds);
  if (error) throw new Error(mensajeErrorPE(error, 'leer los enlaces de los sectores con las bodegas'));
  return (data ?? []) as EnlaceSector[];
}

export async function enlazarSector(companyId: string, e: EnlaceSector, usuarioId: string | null): Promise<EnlaceSector> {
  const { data, error } = await supabase.from('emergency_plan_sector_locations')
    .insert({ company_id: companyId, sector_id: e.sector_id, warehouse_id: e.warehouse_id, zone_id: e.zone_id, created_by: usuarioId })
    .select('id, sector_id, warehouse_id, zone_id');
  if (error) throw new Error(mensajeErrorPE(error, 'enlazar el sector'));
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
  return data[0] as EnlaceSector;
}

export async function quitarEnlace(id: string): Promise<void> {
  const { data, error } = await supabase.from('emergency_plan_sector_locations').delete().eq('id', id).select('id');
  if (error) throw new Error(mensajeErrorPE(error, 'quitar el enlace'));
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
}

// Productos activos de las zonas enlazadas (por product_ids de la zona y por product_locations).
export async function cargarInventarioDe(companyId: string, bodegas: Bodega[], enlaces: Pick<EnlaceSector, 'warehouse_id' | 'zone_id'>[]):
  Promise<{ productos: ProductoInventario[]; ubicaciones: UbicacionProducto[] }> {
  const zonas = enlaces.flatMap(e => {
    const b = bodegas.find(x => x.id === e.warehouse_id);
    return b ? (e.zone_id ? b.zonas.filter(z => z.id === e.zone_id) : b.zonas) : [];
  });
  const zonaIds = Array.from(new Set(zonas.map(z => z.id)));
  if (!zonaIds.length) return { productos: [], ubicaciones: [] };
  const { data: u, error: eu } = await supabase.from('product_locations').select('product_id, zone_id').eq('company_id', companyId).in('zone_id', zonaIds);
  if (eu) throw new Error(mensajeErrorPE(eu, 'leer las ubicaciones de los productos'));
  const ubicaciones = (u ?? []) as UbicacionProducto[];
  const ids = Array.from(new Set([...zonas.flatMap(z => z.product_ids), ...ubicaciones.map(x => x.product_id)]));
  if (!ids.length) return { productos: [], ubicaciones };
  const { data: p, error: ep } = await supabase.from('products').select('id, name, cas_number').eq('company_id', companyId)
    .in('id', ids).is('deleted_at', null).eq('status', 'active');
  if (ep) throw new Error(mensajeErrorPE(ep, 'leer los productos'));
  return { productos: (p ?? []) as ProductoInventario[], ubicaciones };
}
